/**
 * The authenticated-call seam every later screen PRD reads through.
 *
 * `AuthStatusContext` (in `router/`) answers *"is there a session"* for the guard; this one
 * answers *"make a call with it"*. Keeping them separate is deliberate: the router must not
 * depend on the API surface, and a screen must not be able to reach a token — `request()`
 * attaches the bearer and the binding itself, and there is no accessor that hands either out.
 */

import { createContext, useContext } from 'react'
import type { CachedProfile } from '../device-cache/profile'
import type { ApiRequest, ApiResponse } from './transport'
import type { SessionEndReason, SessionState } from './session'

export interface SessionApi extends SessionState {
  /** The only authenticated call in the app. See `session.ts`. */
  request(request: ApiRequest): Promise<ApiResponse>
  endSession(reason: SessionEndReason): Promise<void>
  /**
   * Write through what `PATCH /users/me` echoed back, to `SessionState` and to the device
   * cache. Exposed because the drawer's self-name editor (D133) is a second writer besides
   * onboarding, and there is no `GET /users/me` to ask for the value directly.
   *
   * Since D139 an un-remembered rename no longer *disappears* on reload — the boot refresh
   * returns the profile and the server's value wins — so it comes back one paint late instead.
   * The cache write is what makes the first paint right, and it is the whole of it offline.
   * **It never throws:** the store is a cache, and a failed write used to freeze the caller.
   */
  rememberProfile(profile: CachedProfile): Promise<void>
  /**
   * **Ask for the connection back — D264.**
   *
   * `online: false` is a latch: `request()` sets it on any `NetworkError` and only a *successful*
   * call or refresh clears it. That was safe while something was guaranteed to keep trying, and
   * the browser `online` event was assumed to be that something. It is not: the Android WebView
   * fires it on a connectivity **transition**, so a phone whose data was suspended mid-request and
   * restored without one leaves the client read-only with nothing running that could notice —
   * measured at 7h 21m on a staging device with working internet.
   *
   * Exposed here, beside `endSession`, because this is a session-lifecycle operation rather than a
   * call: the resume trigger has to be able to *ask*, and only `SessionManager` knows how. It runs
   * the bounded 0/1/3-second sequence, is single-flight, and is a no-op on an online session, so a
   * caller cannot turn an app switch into a round trip. It resolves to the resulting `online`.
   */
  recoverConnectivity(): Promise<boolean>
}

export const SessionContext = createContext<SessionApi | undefined>(undefined)

export function useSession(): SessionApi {
  const ctx = useContext(SessionContext)
  if (ctx === undefined) throw new Error('useSession() called outside <AuthProvider>')
  return ctx
}
